import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Puzzle } from "lucide-react";
import { useAdoptedTools, toolName, iconFor } from "../../../../marketplace";
import type { TherapyTool } from "../../../../marketplace";
import {
    openConstellationInMeeting,
    useLaunchConstellationCoupleForSession,
    useLaunchConstellationForSession,
} from "../../../../constellation";
import { openSystemicMapInMeeting, SystemicMapDockActions } from "../../../../systemic_map";
import { boardPath } from "../../../../emdr";
import type { TherapistToolBoard } from "../../../domain/meetingToolBoard";

// Tool slugs this panel can start. Outside a call, two of them open a board in
// a new tab; inside one, every one of them opens in the call, where both
// people hear and see it.
const LAUNCHABLE_SLUGS = new Set(["constellation", "genogram-ecomap", "meditation", "emdr"]);

type Props = {
    sessionId: string | null;
    session?: { type?: string; couple_patient_id?: string };
    /**
     * Whose session this is. The genogram belongs to a patient rather than to
     * a session, so it opens theirs instead of starting something new and
     * unattached.
     */
    subject?: { kind: "patient" | "couple" | "group"; id: string; label?: string } | null;
    /**
     * Opens the meditation panel on the stage. Absent where the panel is shown
     * outside a call — there is nothing to play it into.
     */
    onOpenMeditation?: () => void;
    /** Opens the bilateral stimulation panel on the stage. Absent outside a call. */
    onOpenBilateral?: () => void;
    /**
     * Puts a board — the constellation, or a genogram or ecomap — on the
     * call's stage, the therapist's page on their screen and the patient's on
     * theirs. Absent outside a call, where boards open in a tab of their own.
     */
    onOpenBoard?: (board: TherapistToolBoard) => void;
};

/**
 * Adopted-tools panel shown in the session dock. Lets the therapist start a
 * tool (e.g. constellation) attached to the current session, so the tool's
 * output is saved back to this session.
 */
export default function SessionToolsPanel({
    sessionId,
    session,
    subject = null,
    onOpenMeditation,
    onOpenBilateral,
    onOpenBoard,
}: Props) {
    const { t } = useTranslation();
    const { data: tools, isLoading } = useAdoptedTools();
    const launchConstellation = useLaunchConstellationForSession();
    const launchConstellationCouple = useLaunchConstellationCoupleForSession();
    const [launchingSlug, setLaunchingSlug] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [mapSheetOpen, setMapSheetOpen] = useState(false);

    const launch = async (tool: TherapyTool) => {
        if (tool.slug === "meditation") {
            // Nothing is launched: the library opens on the stage, where the
            // recording plays for the therapist and the patient at once.
            onOpenMeditation?.();
            return;
        }
        if (tool.slug === "emdr") {
            if (onOpenBilateral) {
                // In a call: on the stage, where the patient already is.
                onOpenBilateral();
            } else if (sessionId) {
                // In the consulting room: a board of its own, like the
                // constellation, so the session page stays open.
                window.open(boardPath(sessionId), "_blank", "noopener,noreferrer");
            }
            return;
        }
        if (tool.slug === "genogram-ecomap") {
            // Not attached to the session: a map is a living document about
            // the patient, so this opens theirs — new or existing — rather
            // than starting a fresh one per appointment.
            setMapSheetOpen(true);
            return;
        }
        if (!LAUNCHABLE_SLUGS.has(tool.slug) || !sessionId) return;
        setError(null);
        setLaunchingSlug(tool.slug);
        try {
            if (onOpenBoard) {
                // In a call: on the stage, beside the patient's face, with
                // the patient's own page of the board on their screen.
                const paths = await openConstellationInMeeting(sessionId);
                onOpenBoard({ kind: "constellation", ...paths });
                return;
            }
            const isCouple = tool.slug === "constellation" &&
                session?.type === "couple" &&
                !!session?.couple_patient_id;
            const path = await (isCouple
                ? launchConstellationCouple(sessionId)
                : launchConstellation(sessionId));
            // Open in a new tab so the recording session page stays open.
            window.open(path, "_blank", "noopener,noreferrer");
        } catch {
            setError(t("constellation.therapist.launchError", "Couldn't start the tool."));
        } finally {
            setLaunchingSlug(null);
        }
    };

    /** A map chosen or made in the sheet, put on the call's stage. */
    const openMapInCall = onOpenBoard
        ? async (mapId: string) => {
              setError(null);
              setLaunchingSlug("genogram-ecomap");
              try {
                  const paths = await openSystemicMapInMeeting(mapId);
                  onOpenBoard({ kind: "systemic-map", ...paths });
              } catch {
                  setError(t("systemicMap.meeting.error"));
              } finally {
                  setLaunchingSlug(null);
              }
          }
        : undefined;

    return (
        <div className="h-full overflow-y-auto pt-12 custom-scrollbar">
            <h3 className="mb-4 flex items-center gap-2 px-1 text-base font-semibold text-content-primary">
                <Puzzle className="h-5 w-5 text-accent-text" />
                {t("session.tools", "Tools")}
            </h3>

            {!sessionId && (
                <p className="px-1 text-sm text-content-tertiary">
                    {t("session.toolsNeedSave", "Save the session first to use a tool.")}
                </p>
            )}

            {sessionId && isLoading && (
                <div className="flex justify-center py-8 text-content-tertiary">
                    <Loader2 className="h-5 w-5 animate-spin" />
                </div>
            )}

            {sessionId && !isLoading && (!tools || tools.filter((tl) => LAUNCHABLE_SLUGS.has(tl.slug)).length === 0) && (
                <p className="px-1 text-sm text-content-tertiary">
                    {t("session.noTools", "No tools available.")}
                </p>
            )}

            {sessionId && !isLoading && tools && (
                <ul className="flex flex-col gap-2">
                    {tools
                        .filter(
                            (tool) =>
                                LAUNCHABLE_SLUGS.has(tool.slug) &&
                                // The map needs somebody to be about.
                                (tool.slug !== "genogram-ecomap" || Boolean(subject)) &&
                                // And the meditation needs a call to play into.
                                (tool.slug !== "meditation" || Boolean(onOpenMeditation)),
                        )
                        .map((tool) => {
                            const Icon = iconFor(tool.icon);
                            const busy = launchingSlug === tool.slug;
                            const label = toolName(t, tool);
                            return (
                                <li key={tool.id}>
                                    <button
                                        type="button"
                                        onClick={() => launch(tool)}
                                        disabled={busy}
                                        className="flex w-full items-center gap-3 rounded-xl border border-accent/20 bg-accent-tint px-4 py-3 text-left transition-all hover:bg-accent-tint disabled:opacity-50 border-accent bg-accent-tint"
                                    >
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-contrast">
                                            {busy ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Icon className="h-4 w-4" />
                                            )}
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block truncate text-sm font-semibold text-content-primary">
                                                {label}
                                            </span>
                                            <span className="block text-xs text-accent-text">
                                                {t("session.startTool", "Start")}
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                </ul>
            )}

            {error && (
                <p role="alert" className="mt-3 px-1 text-sm text-red-600">
                    {error}
                </p>
            )}

            {subject && (
                <SystemicMapDockActions
                    isOpen={mapSheetOpen}
                    onClose={() => setMapSheetOpen(false)}
                    // In a call, on its stage; elsewhere in a new tab, like the
                    // constellation board — going to the map would otherwise
                    // take the therapist away from the session.
                    openInNewTab
                    onOpenMap={openMapInCall}
                    subject={{
                        kind: subject.kind,
                        id: subject.id,
                        label: subject.label ?? t("systemicMap.toolName"),
                    }}
                />
            )}
        </div>
    );
}
